/**
 * @lines98/client/screens/replay-screen — playable replay player.
 *
 * INVARIANT (load-bearing — replays-ghosts.md §3.1):
 *   «реплей использует тот же движок, что и реальная игра» —
 *   we import `applyMove` + `startState` from `@lines98/shared`. No local copy.
 *
 * Source of truth (two, §A.2): EITHER the just-finished local game from
 * `loadLastGame()` (own replay opened from the result screen — no fetch) OR a
 * server-fetched PLAYABLE replay passed in by `app.ts` (`/r/<short_id>` route /
 * in-shell leaderboard nav). Both reduce to the canonical `seed + moves[] + mode`
 * (replays-ghosts.md §2.1), which the SHARED engine re-simulates — there is NO
 * separate playback engine. A provided server source WINS; the localStorage path
 * is the fallback for a no-source (own-replay) mount.
 *
 * MIRRORS prototype `replay-overlay` (lines 829-880):
 *   - `openReplay(boardSeed, moves, ...)` → `R = { seed, moves, idx, st, from, to }`.
 *   - `replayApplyNext` — apply moves[idx]; `applyMove(R.st, ...)`.
 *   - `replayRebuildTo(target)` — rewind / fast-forward by re-running from seed.
 *   - `replayStep(±1)`, `replayRestart()`, `replayToggle()` (play/pause), `replaySpeed()`.
 *
 * AC-3 (replay parity): after the final step, R.st.score === saved.score.
 *
 * UX: 9×9 mini-board with same color tokens as the live game, but ALL balls
 * use `ball.static` (no «pop» animation per move — animation would obscure
 * the timeline).
 */

import { applyMove, findPath, startState } from '@lines98/shared';
import { N } from '@lines98/shared/config/balance';
import type { CellValue, GameMode, GameState, Move, MoveEvents } from '@lines98/shared/types/game';

import {
  animateMoveSlide,
  applySpawnPop,
  lineClearCelebrationMs,
  spawnLineClearOverlay,
  spawnLineClearGrowOverlay,
  spawnLineClearHoldOverlay,
  type MoveSlideHandle,
} from '../lib/board-animations.js';
import { mountBoardView, type BoardViewHandle } from '../lib/board-view.js';
import { attachLiveAppearance } from '../lib/board-appearance.js';
import { attachTitleFit } from '../lib/title-fit.js';
import {
  attachReplaySaveButton,
  attachReplayShareButton,
  type ReplaySaveButtonHandle,
} from '../lib/replay-save-button.js';
import { t } from '../lib/i18n/index.js';
import { loadLastGame } from '../services/replay-store.js';
import { useGameStore } from '../store/game-store.js';
import type { ScreenHandle } from './board-screen.js';

const SPEEDS_X = [1, 2, 0.5] as const;
/**
 * Межходовая пауза автоплея — навешивается ПОВЕРХ полного завершения визуалки шага
 * (слайд + хвост-комета + celebration линий), НЕ вместо него (owner-feedback
 * 2026-06-09: «следующий ход не дожидается окончания анимации»). Дыхание между
 * ходами, чтобы зритель успел осознать каждый. Масштабируется регулятором скорости.
 */
const STEP_PAUSE_MS = 360;

/**
 * The minimal playback shape — the ONLY thing the player needs (replays-ghosts.md
 * §2.1). Both sources (server PLAYABLE / local last-game) reduce to this. No
 * score echo is needed: the score is derived by re-simulating `moves` through the
 * SHARED engine (AC-2 — final-frame score equals score_verified by determinism).
 */
export interface ReplayPlayback {
  readonly seed: number;
  readonly moves: Move[];
  readonly mode: GameMode;
}

/**
 * Optional mount options. `source` overrides the localStorage fallback (server
 * PLAYABLE replay). `onExit` overrides the default «back to result/board»
 * close-behaviour (e.g. the `/r/<short_id>` route has nowhere to go back to).
 */
/**
 * Кнопка-дискета «Сохранить в Книгу» (тикет B) — показывается ТОЛЬКО когда реплей ЧУЖОЙ
 * (серверный flow remote-replay → foreign playable/teaser). На своём реплее опция НЕ
 * передаётся → дискеты нет (AC-B1, свои авто-архивируются). Решение «чужой/свой» —
 * серверное (передаётся хостом), НЕ клиентский guess.
 */
export interface ForeignSaveOptions {
  /** short_id чужой партии (натур-ключ закладки). */
  readonly shortId: string;
  /** Уже в Книге? (server-truth). */
  readonly savedInitially?: boolean;
  /** Гость? → клик дискеты ведёт на логин (AC-B6). */
  readonly isGuest?: boolean;
  /** Открыть поверхность логина (общий auth-flow) — хост прокидывает. */
  readonly onRequestLogin: () => void;
}

/**
 * Кнопка «Поделиться» (реплей-под-волна AC-8/9/10) — задаётся ТОЛЬКО для СВОЕГО реплея
 * (server-truth `isOwn === true` из remote-replay flow). Когда задана — share-kit
 * монтируется в тот же слот `#rp-save-slot` ВМЕСТО дискеты (свои авто-архивируются →
 * ручка тут = шаринг, не save). Взаимоисключающа с `foreignSave` (own → share, foreign →
 * save; хост выставляет ровно одну из них).
 */
export interface OwnShareOptions {
  /** short_id своей партии — для прямой ссылки `/r/<short_id>`. */
  readonly shortId: string;
}

export interface ReplayScreenOptions {
  readonly source?: ReplayPlayback;
  readonly onExit?: () => void;
  /**
   * Дискета «Сохранить в Книгу» (тикет B) — задаётся ТОЛЬКО для ЧУЖОГО реплея. Когда
   * задана — kit монтируется в шапку плеера (ВНЕ сетки транспорта, AC-B5). Отсутствует
   * → дискеты нет (свой реплей / результат своей партии).
   */
  readonly foreignSave?: ForeignSaveOptions;
  /**
   * Кнопка «Поделиться» (реплей-под-волна AC-8) — задаётся ТОЛЬКО для СВОЕГО реплея
   * (`isOwn === true`). Монтируется в тот же `#rp-save-slot` ВМЕСТО дискеты. Взаимо-
   * исключающа с `foreignSave` (хост раздаёт ровно одну ветку по server-truth `isOwn`).
   */
  readonly ownShare?: OwnShareOptions;
}

interface ReplayElements {
  playWrap: HTMLElement;
  fillEl: HTMLElement;
  playBtn: HTMLButtonElement;
  speedBtn: HTMLButtonElement;
  restartBtn: HTMLButtonElement;
  backBtn: HTMLButtonElement;
  fwdBtn: HTMLButtonElement;
  closeBtn: HTMLButtonElement;
}

interface ReplayRuntime {
  /** Normalized playback (seed + moves + mode) — from server source OR last-game. */
  playback: ReplayPlayback;
  /** Optional override for the close (×) action. */
  onExit?: () => void;
  /** Единый модуль доски (lib/board-view) — рендер поля/плашки/ниши воспроизведения. */
  board: BoardViewHandle;
  state: GameState;
  idx: number; // # of moves applied so far
  from: { r: number; c: number } | null;
  to: { r: number; c: number } | null;
  timerId: number | null;
  speedIdx: number;
  /** True while a move slide / celebration is playing (edge 7.5: gate next step). */
  animating: boolean;
  /** Active move-slide handle (cancelled on rewind/unmount mid-slide). */
  slide: MoveSlideHandle | null;
  /** Pending «schedule next autoplay step» timer (separate from the slide). */
  nextStepTimer: number | null;
}

export function mountReplayScreen(
  host: HTMLElement,
  opts: ReplayScreenOptions = {},
): ScreenHandle {
  // Server PLAYABLE source wins; otherwise fall back to the last local game.
  const playback: ReplayPlayback | null = opts.source ?? loadLastGameAsPlayback();
  if (!playback) {
    // No source and no saved game — bounce back to board (only happens for the
    // own-replay path, which always has a last game when reachable).
    useGameStore.getState().startNewGame();
    useGameStore.setState({ phase: 'board' });
    return { unmount: () => {} };
  }

  host.innerHTML = '';
  const root = document.createElement('div');
  // .screen-root — ствол-класс edge-clearance (safe-area-aware, global.css).
  root.className = 'replay-screen screen-root';
  root.innerHTML = template();
  host.appendChild(root);

  const elements = collectElements(root);
  // Доска воспроизведения — ЕДИНЫЙ модуль (lib/board-view), статичный режим. aria-label
  // = видимая игроку подпись → через i18n (game.replay = «Просмотр игры», AC-П3-1; НЕ
  // кириллический литерал «реплей»).
  const board = mountBoardView(elements.playWrap, {
    interactive: false,
    ariaLabel: t('game.replay'),
  });
  // Оформление зрителя применяется к доске ЛЮБОГО реплея (AC-3): реплей seed+moves
  // идентичен при любом оформлении (оформление автора не часть партии, §2.2/§4.3).
  const appearance = attachLiveAppearance(board.frame);

  const runtime: ReplayRuntime = {
    playback,
    ...(opts.onExit ? { onExit: opts.onExit } : {}),
    board,
    state: startState(playback.seed),
    idx: 0,
    from: null,
    to: null,
    timerId: null,
    speedIdx: 0,
    animating: false,
    slide: null,
    nextStepTimer: null,
  };

  const teardown = wire(elements, runtime);
  render(elements, runtime);

  // Заголовок — общий модуль lib/title-fit (единый размер = шрифту кнопки).
  const titleFit = attachTitleFit(root);

  // §5.9 floor доски реплея УДАЛЁН (owner-directive 2026-06-24 №1, отмена §5.9-исключения):
  // доска реплея ВСЕГДА вписана во вьюпорт как живая (board-as-etalon, глобальный
  // `--cell-size` из viewport-sizer) и НИКОГДА не больше экрана. Локальный override
  // (lib/replay-board-floor) был ИСТОЧНИКОМ бага «доска больше экрана» → снят целиком.
  // Крупнее на узком touch-landscape становятся ТОЛЬКО транспорт-кнопки (replay.css).

  // Кнопка плеера в едином слоте `#rp-save-slot` (ВНЕ сетки транспорта, AC-12) — РОВНО
  // одна ветка по server-truth `isOwn` (реплей-под-волна AC-8/10):
  //   · СВОЙ реплей (ownShare задан) → кнопка «Поделиться» (ICON_SHARE) → copyShareLink;
  //   · ЧУЖОЙ реплей (foreignSave задан) → дискета «Сохранить» (общий kit, тикет B);
  //   · результат своей локальной партии (ни одна не задана) → слот пуст.
  // Обе ветки — один общий модуль lib/replay-save-button (НЕ дублируем по экранам).
  let saveButton: ReplaySaveButtonHandle | null = null;
  const slot = root.querySelector<HTMLElement>('#rp-save-slot');
  if (slot) {
    if (opts.ownShare) {
      saveButton = attachReplayShareButton(opts.ownShare);
      slot.appendChild(saveButton.element);
    } else if (opts.foreignSave) {
      saveButton = attachReplaySaveButton(opts.foreignSave);
      slot.appendChild(saveButton.element);
    }
  }

  // Плашка «Дуэль» УБРАНА с экрана просмотра партии (owner-directive 2026-06-21):
  // дуэль (гонка против призрака) живёт в разделе ПОМОЩЬ отдельной контент-вкладкой,
  // а НЕ здесь. На просмотре партии она к тому же ломала раскладку (горизонтальный
  // overflow в landscape, вертикальный в near-square, коллизия transport↔доска на
  // 320×480). Переименование «Реплей»→«Просмотр игры» (t('game.replay')) сохранено.

  // «?»-попап в реплее УБРАН полностью (owner-directive 2026-05-30): описательное
  // (seed/ходы) здесь не показываем.

  return {
    unmount(): void {
      stop(runtime);
      teardown();
      appearance.detach();
      runtime.board.unmount();
      titleFit.detach();
      saveButton?.destroy();
      host.innerHTML = '';
    },
  };
}

// =============================================================================
// Template
// =============================================================================

function template(): string {
  // P2 «сэндвич-меню» (≡ + dropdown) УДАЛЕНО (deprecated T-7.2.5-fix1: side-rail
  // fit-switch + stacked compact покрывают все viewport без него). Все 6 кнопок
  // транспорта рендерятся прямо в .replay-transport.
  // «ПОСТАМЕНТ» canon (design-tokens-unified.md §14.10, mirrors board-screen):
  // chrome (header / progress / transport) wrapped in `.replay-rail`. Portrait:
  // `display: contents` → children participate in the screen grid by their
  // grid-area (header / progress / transport rows, board between). Rail layout:
  // `.replay-rail` becomes the absolute corner-anchored panel (title top-right,
  // transport bottom-right) while `.play-wrap` (board) is true-centred.
  return `
    <div class="replay-rail">
      <div class="replay-header">
        <div class="replay-title-row">
          <h2 class="replay-title screen-title">${t('game.replay')}</h2>
          <!-- Слот дискеты «Сохранить в Книгу» (тикет B): ВНЕ сетки транспорта (AC-B5 —
               7-й контрол ломал 2D-раскладку на 320×480; инцидент «Дуэль» 2026-06-21).
               kit монтируется сюда ТОЛЬКО для чужого реплея; на своём слот пуст. -->
          <div class="replay-save-slot" id="rp-save-slot"></div>
        </div>
      </div>

      <!-- Тонкая полоса прогресса — ВПЛОТНУЮ НАД кнопками (owner-directive
           2026-05-30): grid-area progress стоит между доской и транспортом. -->
      <div class="replay-progress">
        <div class="replay-bar"><div class="fill" id="rp-fill"></div></div>
      </div>

      <!-- Транспорт — РОВНО 2 ряда (3-кол сетка × 6 кнопок = 3+3). «К итогу» теперь
           6-я кнопка-ИКОНКА выхода (owner-directive 2026-05-30: текст растягивал
           кнопку, мешал уместить всё в 2 ряда). Высота как у игровых (не --compact). -->
      <div class="replay-transport">
        <button class="btn" id="rp-restart" type="button" title="${t('figures.rpRestart')}">⏮</button>
        <button class="btn" id="rp-back" type="button" title="${t('figures.rpBack')}">◀</button>
        <button class="btn primary" id="rp-play" type="button" title="${t('figures.rpPlay')}">▶</button>
        <button class="btn" id="rp-fwd" type="button" title="${t('figures.rpFwd')}">▶|</button>
        <button class="btn" id="rp-speed" type="button" title="${t('figures.rpSpeed')}"><span class="replay-speed">1×</span></button>
        <button class="btn replay-exit" id="rp-close" type="button" title="${t('figures.rpExit')}" aria-label="${t('figures.rpExit')}"><svg class="replay-exit-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M14 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2"/><path d="M9 12h12l-3 -3"/><path d="M18 15l3 -3"/></svg></button>
      </div>
    </div>

    <!-- Доску воспроизведения монтирует ЕДИНЫЙ модуль board-view в .play-wrap при
         mount — отдельной разметки доски здесь нет (доска = один объект). -->
    <div class="play-wrap" id="rp-play-wrap"></div>
  `;
}

// =============================================================================
// Element collection
// =============================================================================

function collectElements(root: HTMLElement): ReplayElements {
  return {
    playWrap: must(root.querySelector<HTMLElement>('#rp-play-wrap')),
    fillEl: must(root.querySelector<HTMLElement>('#rp-fill')),
    playBtn: must(root.querySelector<HTMLButtonElement>('#rp-play')),
    speedBtn: must(root.querySelector<HTMLButtonElement>('#rp-speed')),
    restartBtn: must(root.querySelector<HTMLButtonElement>('#rp-restart')),
    backBtn: must(root.querySelector<HTMLButtonElement>('#rp-back')),
    fwdBtn: must(root.querySelector<HTMLButtonElement>('#rp-fwd')),
    closeBtn: must(root.querySelector<HTMLButtonElement>('#rp-close')),
  };
}

// =============================================================================
// Wiring + transport
// =============================================================================

function wire(el: ReplayElements, runtime: ReplayRuntime): () => void {
  el.closeBtn.addEventListener('click', () => {
    stop(runtime);
    if (runtime.onExit) {
      // Caller decides (e.g. /r/<short_id> route: nowhere to go back → start own daily).
      runtime.onExit();
      return;
    }
    // Default: back to result if a finished game is still in the store, else board.
    const s = useGameStore.getState();
    useGameStore.setState({ phase: s.finishReason ? 'result' : 'board' });
  });

  el.playBtn.addEventListener('click', () => togglePlay(el, runtime));
  el.restartBtn.addEventListener('click', () => {
    stop(runtime);
    rebuildTo(0, runtime);
    render(el, runtime);
  });
  el.backBtn.addEventListener('click', () => {
    stop(runtime);
    rebuildTo(Math.max(0, runtime.idx - 1), runtime);
    render(el, runtime);
  });
  el.fwdBtn.addEventListener('click', () => {
    // Single step ◀▶ — пауза автоплея + проиграть ОДИН ход с анимацией
    // (подсветка→слайд→сбор/спавн). Edge 7.5: если анимация идёт — игнор (ждём).
    stop(runtime);
    playOneStep(el, runtime, false);
  });
  el.speedBtn.addEventListener('click', () => {
    cycleSpeed(el, runtime);
  });

  // Сэндвич-меню (≡) удалено — у wire() больше нет document-слушателей; teardown пуст.
  return () => {};
}

function cycleSpeed(el: ReplayElements, runtime: ReplayRuntime): void {
  runtime.speedIdx = (runtime.speedIdx + 1) % SPEEDS_X.length;
  const label = `${SPEEDS_X[runtime.speedIdx]}×`;
  el.speedBtn.querySelector('.replay-speed')!.textContent = label;
  // Новая скорость применится к СЛЕДУЮЩЕМУ шагу автоплея (текущая анимация
  // доигрывает на старой скорости — прерывать её в середине дёргано).
}

function togglePlay(el: ReplayElements, runtime: ReplayRuntime): void {
  if (isPlaying(runtime)) {
    stop(runtime);
    el.playBtn.textContent = '▶';
  } else {
    play(el, runtime);
  }
}

/** Playing = автоплей активен (анимация хода ИЛИ запланирован следующий шаг). */
function isPlaying(runtime: ReplayRuntime): boolean {
  return runtime.timerId !== null || runtime.nextStepTimer !== null || runtime.animating;
}

/**
 * Запустить автоплей. С конца — перематываем в начало. Автоплей = цепочка
 * анимированных шагов: каждый шаг (подсветка→слайд→сбор/спавн) самопланирует
 * следующий ПОСЛЕ своей анимации + паузы (edge 7.5 — не наезжаем на анимацию).
 */
function play(el: ReplayElements, runtime: ReplayRuntime): void {
  if (runtime.idx >= runtime.playback.moves.length) {
    rebuildTo(0, runtime);
    render(el, runtime);
  }
  el.playBtn.textContent = '⏸';
  // Маркер «автоплей активен» — timerId служит флагом «в режиме play»; реальный
  // тайминг между шагами держит nextStepTimer внутри цепочки playOneStep.
  runtime.timerId = 1;
  playOneStep(el, runtime, true);
}

/** Остановить автоплей + отменить любую идущую анимацию/запланированный шаг. */
function stop(runtime: ReplayRuntime): void {
  runtime.timerId = null;
  if (runtime.nextStepTimer !== null) {
    window.clearTimeout(runtime.nextStepTimer);
    runtime.nextStepTimer = null;
  }
  if (runtime.slide) {
    runtime.slide.cancel();
    runtime.slide = null;
  }
  runtime.animating = false;
}

/**
 * Проиграть ОДИН ход с анимацией (board-feedback-animations.md §2.5):
 *   1. подсветка источника (sel) + цели (path) на pre-move state;
 *   2. слайд шара по `findPath(pre-grid, from, to)` (lib/board-animations);
 *   3. seat (голова доехала, totalMs) — `applyMove` (тот же движок, детерминизм
 *      §3.1) → рендер post-move state; если линия собрана (`MoveEvents.cleared`) —
 *      live-celebration overlay'и (§2.1); если спавн без сбора — spawn-pop на новых
 *      шарах (§2.5 п.4); счёт-pop обновляется в рендере. Шар УЖЕ стоит, но позади
 *      доезжает хвост-комета, а celebration только стартовал → шаг ещё НЕ закончен;
 *   4. trailDone (хвост-комета убрана) — `animating` снимается; для автоплея здесь
 *      планируется следующий шаг — но лишь ПОСЛЕ остатка celebration (если он
 *      переживает хвост) ПЛЮС межходовая пауза поверх. Так следующий ход НЕ наезжает
 *      ни на хвост, ни на сбор предыдущего (owner-feedback 2026-06-09).
 *
 * `chain=true` (автоплей) → trailDone планирует следующий шаг (см. п.4).
 * `chain=false` (одиночный ◀▶) → один шаг, без планирования.
 *
 * INVARIANT — тот же движок, что в живой игре: `applyMove` из `@lines98/shared`;
 * визуал пути — `findPath` (оба детерминированы). Edge 7.5: пока `animating` (= вся
 * визуалка шага, включая хвост+celebration, не догорела), новый шаг игнорируется.
 */
function playOneStep(el: ReplayElements, runtime: ReplayRuntime, chain: boolean): void {
  if (runtime.animating) return;
  if (runtime.idx >= runtime.playback.moves.length) {
    // В конце — остановить автоплей.
    runtime.timerId = null;
    el.playBtn.textContent = '▶';
    return;
  }

  const m = runtime.playback.moves[runtime.idx]!;
  const speed = SPEEDS_X[runtime.speedIdx]!;

  // Snapshot pre-move grid для findPath (визуал) + детект спавна после хода.
  const preGrid = cloneGrid(runtime.state.grid);
  const color = preGrid[m.sr]?.[m.sc] ?? 0;
  const path = findPath(preGrid, { r: m.sr, c: m.sc }, { r: m.r, c: m.c });

  // Pre-move render: источник ещё на месте, подсветка from/to.
  runtime.from = { r: m.sr, c: m.sc };
  runtime.to = { r: m.r, c: m.c };
  render(el, runtime);
  // Скрыть статичный шар-источник на время слайда (его «играет» overlay) — иначе
  // дубль: и статичный на месте, и едущий overlay. Post-move render источник сам
  // обнулит (шар переехал).
  const srcBall = runtime.board.cells[m.sr * N + m.sc]?.querySelector<HTMLElement>('.ball');
  if (srcBall) srcBall.style.visibility = 'hidden';

  runtime.animating = true;
  // Длительность celebration этого хода — узнаём только ПОСЛЕ applyMove (тогда есть
  // `events`). Объявляем тут, заполняем в seat; читаем в trailDone (см. ниже).
  // `celebrationStartedAt` — wall-clock момент запуска overlay'ев сбора (= seat),
  // чтобы в trailDone посчитать ОСТАТОК сбора без дублирования slide-таймингов в
  // экране (модульная дисциплина: длительности живут в board-animations).
  let celebrationMs = 0;
  let celebrationStartedAt = 0;

  // seat (= onDone) — голова доехала: применяем ход тем же движком (state → истина) и
  // рендерим post-state + celebration. НО шаг ещё НЕ закончен визуально: позади
  // доезжает хвост-комета, а сбор линии (celebration) только стартовал. Поэтому
  // `animating` тут НЕ снимаем и следующий шаг НЕ планируем — это делает trailDone.
  const seat = (): void => {
    if (!runtime.animating) return; // отменено (stop) во время слайда
    // Применяем ход тем же движком (state → истина), снимаем события.
    const res = applyMove(runtime.state, m.sr, m.sc, m.r, m.c);
    const events: MoveEvents | null = res.ok && res.events ? res.events : null;
    runtime.idx++;
    // Post-move render (источник пуст, шар сел/линия очищена). Спавн-клетки
    // определяем диффом pre→post (только если сбора не было — иначе celebration).
    const spawned = events ? [] : diffSpawned(preGrid, runtime.state.grid, m);
    render(el, runtime);
    celebrate(runtime, events, spawned, speed);
    // Сколько ещё гореть overlay'ям сбора — спрашиваем у того же модуля, что их
    // запускает (board-animations: единый источник чисел 600/800; экран их НЕ
    // хардкодит). Хвост-комету ждём через trailDone-колбэк (отдельный сигнал).
    celebrationMs = lineClearCelebrationMs(events, speed);
    celebrationStartedAt = performance.now();
  };

  // trailDone (= onTrailDone) — хвост-комета ПОЛНОСТЬЮ убран с доски. Это «слайд
  // целиком догорел»; теперь дожидаемся ещё и celebration (если был длиннее хвоста),
  // и только ПОТОМ — межходовая пауза → следующий шаг. Так следующий ход НЕ наезжает
  // ни на хвост, ни на сбор предыдущего (owner-feedback 2026-06-09 «ход не дожидается
  // окончания анимации»). seat всегда успевает раньше (cleanup ≥ totalMs ≥ seat).
  const trailDone = (): void => {
    runtime.slide = null;
    if (!runtime.animating) return; // отменено (stop) во время слайда/хвоста
    runtime.animating = false;

    if (!chain) return; // одиночный ◀▶ — без планирования следующего

    if (runtime.timerId === null || runtime.idx >= runtime.playback.moves.length) {
      // Автоплей остановлен пользователем ИЛИ достигнут конец — стоп.
      runtime.timerId = null;
      el.playBtn.textContent = '▶';
      return;
    }
    // Хвост уже убран на этом тике; celebration мог стартовать в seat (на totalMs) и
    // ещё гореть. Ждём ОСТАТОК celebration (если он переживает хвост), ПЛЮС межходовую
    // паузу поверх — и лишь затем следующий шаг. Остаток считаем по wall-clock от
    // момента старта overlay'ев (а НЕ дублируя slide-тайминги в экране).
    const celebrationElapsedMs = celebrationMs > 0 ? performance.now() - celebrationStartedAt : 0;
    const celebrationRemainMs = Math.max(0, celebrationMs - celebrationElapsedMs);
    const waitMs = celebrationRemainMs + STEP_PAUSE_MS / speed;
    runtime.nextStepTimer = window.setTimeout(() => {
      runtime.nextStepTimer = null;
      playOneStep(el, runtime, true);
    }, waitMs);
  };

  if (path && path.length >= 2 && color !== 0) {
    runtime.slide = animateMoveSlide(
      runtime.board.boardEl,
      runtime.board.cells,
      color,
      path,
      speed,
      seat,
      trailDone,
    );
  } else {
    // Нет пути для визуала (не должно случаться для зачтённого хода) или from===to
    // → без слайда: applyMove+render (seat), затем сразу trailDone (хвоста нет).
    seat();
    trailDone();
  }
}

/**
 * Проиграть celebration после хода: либо line-clear overlay'и (§2.1, через
 * контракт `MoveEvents`), либо spawn-pop на новых шарах (§2.5 п.4). Цвета/варианты
 * — те же, что в живой игре (общий модуль lib/board-animations).
 */
function celebrate(
  runtime: ReplayRuntime,
  events: MoveEvents | null,
  spawned: ReadonlyArray<{ r: number; c: number }>,
  speed: number,
): void {
  if (events && events.cleared.length > 0) {
    for (const cb of events.cleared) {
      const cell = runtime.board.cells[cb.r * N + cb.c];
      if (!cell) continue;
      if (events.viaSpawn) {
        if (cb.grown) spawnLineClearGrowOverlay(cell, cb.color, speed);
        else spawnLineClearHoldOverlay(cell, cb.color, speed);
      } else {
        spawnLineClearOverlay(cell, cb.color, speed);
      }
    }
    return;
  }
  // Спавн без сбора — новые шары «появляются» по ходу (§2.5 п.4, не нарушает
  // AC-13: это спавн ПО ХОДУ, не стартовая раскладка).
  for (const p of spawned) {
    const ball = runtime.board.cells[p.r * N + p.c]?.querySelector<HTMLElement>('.ball:not(.ghost)');
    if (ball) applySpawnPop(ball);
  }
}

/** Клетки, ставшие непустыми после хода и НЕ являющиеся целью хода (= spawned). */
function diffSpawned(
  pre: CellValue[][],
  post: readonly CellValue[][],
  move: Move,
): Array<{ r: number; c: number }> {
  const out: Array<{ r: number; c: number }> = [];
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      if (r === move.r && c === move.c) continue; // destination = переехавший шар
      const was = pre[r]?.[c] ?? 0;
      const now = post[r]?.[c] ?? 0;
      if (was === 0 && now !== 0) out.push({ r, c });
    }
  }
  return out;
}

/** Deep-clone grid (для pre-move snapshot — findPath + spawn-diff). */
function cloneGrid(g: readonly CellValue[][]): CellValue[][] {
  return g.map((row) => [...row]);
}

/** Rebuild engine state from `seed`, replay first `target` moves. */
function rebuildTo(target: number, runtime: ReplayRuntime): void {
  runtime.state = startState(runtime.playback.seed);
  runtime.idx = 0;
  runtime.from = null;
  runtime.to = null;
  const clamped = Math.max(0, Math.min(target, runtime.playback.moves.length));
  while (runtime.idx < clamped) {
    const m = runtime.playback.moves[runtime.idx]!;
    applyMove(runtime.state, m.sr, m.sc, m.r, m.c);
    runtime.idx++;
  }
  if (clamped > 0) {
    const last = runtime.playback.moves[clamped - 1]!;
    runtime.from = { r: last.sr, c: last.sc };
    runtime.to = { r: last.r, c: last.c };
  }
}

// =============================================================================
// Rendering
// =============================================================================

function render(el: ReplayElements, runtime: ReplayRuntime): void {
  const { grid, next, score } = runtime.state;
  const { board } = runtime;

  // Поле + плашка (Очки/Ходы/ниша) — через ЕДИНЫЙ модуль доски. Призраки на
  // клетках — только в predict (replays-ghosts.md §2.3); ниша скрыта в «вслепую».
  board.renderStaticGrid(grid, runtime.playback.mode === 'predict' ? next : undefined);
  board.setScore(score);
  board.setMoves(runtime.idx);
  board.renderNiche(next, runtime.playback.mode !== 'blind');

  // Подсветка текущего хода: откуда (sel) / куда (path). Сбрасываем и ставим.
  for (const cell of board.cells) cell.classList.remove('sel', 'path');
  if (runtime.from) board.cells[runtime.from.r * N + runtime.from.c]?.classList.add('sel');
  if (runtime.to) board.cells[runtime.to.r * N + runtime.to.c]?.classList.add('path');

  // Прогресс — только полоса (текст «Ход/итог» убран).
  const pct = runtime.playback.moves.length
    ? (runtime.idx / runtime.playback.moves.length) * 100
    : 0;
  el.fillEl.style.width = `${pct.toFixed(2)}%`;
}

// =============================================================================
// Helpers
// =============================================================================

function must<T>(value: T | null): T {
  if (value === null || value === undefined) {
    throw new Error('replay-screen: required element missing in template');
  }
  return value;
}

/**
 * Load the last finished local game and reduce it to `ReplayPlayback`. Returns
 * null if there is no saved game (own-replay path only — the server-source path
 * never reaches this). The score echo in `SavedGame` is intentionally dropped —
 * the player derives the score by re-simulation (AC-2).
 */
function loadLastGameAsPlayback(): ReplayPlayback | null {
  const saved = loadLastGame();
  if (!saved) return null;
  return { seed: saved.seed, moves: saved.moves, mode: saved.mode };
}


// Borrowed types — kept narrow to avoid circular imports.
export type { Move };
